import { useRef, useEffect, useCallback } from 'react';
import { syllabusScanService, SCAN_STATUS } from '../services/SyllabusScanService';
import { UsageEventsTracker } from '../services/UsageEventsTracker';

function getScanStatusLabel(isProcessing, isComplete, isError) {
  if (isError) return 'error';
  if (isComplete) return 'complete';
  if (isProcessing) return 'scanning';
  return 'pending';
}

/**
 * Encapsulates all analytics tracking for the ScanWaitStep:
 * - Scan lifecycle events (started / completed / error / timeout)
 * - Video engagement (selection, watch duration, idle)
 * - Navigation handlers that combine UI action + analytics
 *
 * Returns wrapped handlers the component wires to its buttons/tiles.
 */
export function useScanWaitAnalytics({
  scanIds,
  primaryScanId,
  result,
  error,
  isProcessing,
  isComplete,
  isError,
  selectedVideo,
  setSelectedVideo,
  goNext,
  goBack,
  goToStep,
}) {
  // ── Refs ──────────────────────────────────────────────────────────
  const stepEnteredAtRef = useRef(Date.now());
  const videoSelectedAtRef = useRef(Date.now());
  const videosWatchedRef = useRef([]);
  const idleTimerRef = useRef(null);
  const scanStartedEmittedRef = useRef(false);
  const scanCompletedEmittedRef = useRef(false);
  const scanErrorEmittedRef = useRef(false);
  const selectedVideoRef = useRef(selectedVideo);
  const scanStatusRef = useRef({ isProcessing, isComplete, isError });

  // Keep refs in sync
  useEffect(() => { selectedVideoRef.current = selectedVideo; }, [selectedVideo]);
  useEffect(() => { scanStatusRef.current = { isProcessing, isComplete, isError }; }, [isProcessing, isComplete, isError]);

  // ── Idle timer ────────────────────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      const { isComplete: c, isError: e, isProcessing: p } = scanStatusRef.current;
      if (!c && !e) {
        UsageEventsTracker.recordUserAction('ONBOARDING_SCAN_VIDEO', {
          action: 'idle',
          category: 'onboarding',
          label: 'scan-wait',
          metadata: {
            idleDurationMs: 30000,
            currentVideoId: selectedVideoRef.current.id,
            scanStatus: getScanStatusLabel(p, c, e),
          },
        });
      }
    }, 30000);
  }, []);

  // ── Scan lifecycle events ─────────────────────────────────────────
  useEffect(() => {
    if (scanStartedEmittedRef.current) return;
    scanStartedEmittedRef.current = true;
    stepEnteredAtRef.current = Date.now();
    videoSelectedAtRef.current = Date.now();

    UsageEventsTracker.recordUserAction('ONBOARDING_SCAN', {
      action: 'scan_started',
      category: 'onboarding',
      label: 'scan-wait',
      metadata: {
        scanCount: scanIds.length || 1,
        mode: (scanIds.length || 1) > 1 ? 'multi' : 'single',
      },
    });

    resetIdleTimer();
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isComplete || scanCompletedEmittedRef.current) return;
    scanCompletedEmittedRef.current = true;

    const scan = primaryScanId ? syllabusScanService.getScan(String(primaryScanId)) : null;
    const scanDurationMs = (scan?.completedAt && scan?.startedAt) ? scan.completedAt - scan.startedAt : null;
    const totalWaitDurationMs = Date.now() - stepEnteredAtRef.current;
    const coursesDetected = result?.courses?.length || result?.course ? 1 : 0;

    let scansCompleted = 0, scansErrored = 0;
    for (const id of scanIds) {
      const s = syllabusScanService.getScan(String(id));
      if (s?.status === SCAN_STATUS.COMPLETE) scansCompleted++;
      if (s?.status === SCAN_STATUS.ERROR) scansErrored++;
    }

    UsageEventsTracker.recordUserAction('ONBOARDING_SCAN', {
      action: 'scan_completed',
      category: 'onboarding',
      label: 'scan-wait',
      metadata: { scanDurationMs, totalWaitDurationMs, coursesDetected, scanCount: scanIds.length || 1, scansCompleted, scansErrored },
    });
  }, [isComplete, primaryScanId, result, scanIds]);

  useEffect(() => {
    if (!isError || scanErrorEmittedRef.current) return;
    scanErrorEmittedRef.current = true;

    const scan = primaryScanId ? syllabusScanService.getScan(String(primaryScanId)) : null;
    const scanDurationMs = (scan?.completedAt && scan?.startedAt) ? scan.completedAt - scan.startedAt : null;
    const elapsed = scan?.startedAt ? Date.now() - scan.startedAt : null;
    const isTimeout = elapsed && elapsed > 5 * 60 * 1000;

    UsageEventsTracker.recordUserAction('ONBOARDING_SCAN', {
      action: isTimeout ? 'scan_timeout' : 'scan_error',
      category: 'onboarding',
      label: 'scan-wait',
      metadata: {
        scanDurationMs,
        ...(isTimeout ? { elapsedMs: elapsed } : {}),
        errorMessage: typeof error === 'string' ? error : 'Scan failed',
        scanId: primaryScanId,
      },
    });
  }, [isError, primaryScanId, error]);

  // ── Video engagement ──────────────────────────────────────────────
  const emitVideoDuration = useCallback((videoId, videoTitle) => {
    const now = Date.now();
    const viewDurationMs = now - videoSelectedAtRef.current;
    if (viewDurationMs > 500) {
      videosWatchedRef.current.push({ videoId, videoTitle, durationMs: viewDurationMs });
      const { isProcessing: p, isComplete: c, isError: e } = scanStatusRef.current;
      UsageEventsTracker.recordUserAction('ONBOARDING_SCAN_VIDEO', {
        action: 'video_duration',
        category: 'onboarding',
        label: 'scan-wait',
        metadata: { videoId, videoTitle, viewDurationMs, scanStatus: getScanStatusLabel(p, c, e) },
      });
    }
    videoSelectedAtRef.current = now;
  }, []);

  useEffect(() => {
    return () => {
      const v = selectedVideoRef.current;
      if (v?.id) emitVideoDuration(v.id, v.title);
    };
  }, [emitVideoDuration]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleVideoSelect = useCallback((video) => {
    const previousVideoId = selectedVideoRef.current.id;
    emitVideoDuration(selectedVideoRef.current.id, selectedVideoRef.current.title);
    setSelectedVideo(video);
    resetIdleTimer();

    const { isProcessing: p, isComplete: c, isError: e } = scanStatusRef.current;
    UsageEventsTracker.recordUserAction('ONBOARDING_SCAN_VIDEO', {
      action: 'video_selected',
      category: 'onboarding',
      label: 'scan-wait',
      metadata: {
        videoId: video.id, videoTitle: video.title, previousVideoId,
        timeSinceStepEntryMs: Date.now() - stepEnteredAtRef.current,
        scanStatus: getScanStatusLabel(p, c, e),
      },
    });
  }, [emitVideoDuration, resetIdleTimer, setSelectedVideo]);

  const handleReviewClick = useCallback(() => {
    const scan = primaryScanId ? syllabusScanService.getScan(String(primaryScanId)) : null;
    const scanDurationMs = (scan?.completedAt && scan?.startedAt) ? scan.completedAt - scan.startedAt : null;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'review_click', category: 'onboarding', label: 'scan-wait',
      metadata: { waitDurationMs: Date.now() - stepEnteredAtRef.current, scanDurationMs, videosWatched: videosWatchedRef.current },
    });
    goNext();
  }, [goNext, primaryScanId]);

  const handleSkipScan = useCallback(() => {
    const { isProcessing: p, isComplete: c, isError: e } = scanStatusRef.current;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'scan_skip', category: 'onboarding', label: 'scan-wait',
      metadata: { scanStatus: getScanStatusLabel(p, c, e), waitDurationMs: Date.now() - stepEnteredAtRef.current, videosWatched: videosWatchedRef.current },
    });
    goToStep('complete');
  }, [goToStep]);

  const handleManualFallback = useCallback(() => {
    const scan = primaryScanId ? syllabusScanService.getScan(String(primaryScanId)) : null;
    const scanDurationMs = (scan?.completedAt && scan?.startedAt) ? scan.completedAt - scan.startedAt : null;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'manual_fallback', category: 'onboarding', label: 'scan-wait',
      metadata: { scanDurationMs, errorMessage: typeof error === 'string' ? error : 'Scan failed' },
    });
    goToStep('course-review-0');
  }, [goToStep, primaryScanId, error]);

  const handleBack = useCallback(() => {
    const { isProcessing: p, isComplete: c, isError: e } = scanStatusRef.current;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'scan_back', category: 'onboarding', label: 'scan-wait',
      metadata: { scanStatus: getScanStatusLabel(p, c, e), waitDurationMs: Date.now() - stepEnteredAtRef.current },
    });
    goBack();
  }, [goBack]);

  return { handleVideoSelect, handleReviewClick, handleSkipScan, handleManualFallback, handleBack };
}
